import { HOME_ROUTE } from '@/routes';
import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';

import { env } from '@env';
import {
  POST_MESSAGE_DESTINATION_TYPES,
  POST_MESSAGE_ORIGIN_TYPES,
  POST_MESSAGE_TYPES,
} from '@/@types/post-message.types';
import { AppSession } from '@/@types/session.types';
import getServerSession from '@/actions/getServerSession';
import signOutAction from '@/actions/signOut';

interface CachedSession {
  accessToken: string | null;
  refreshToken: string | null;
  cacheExpiresAt: number;
}

const axiosApiInstance = axios.create({
  baseURL: env.NEXT_PUBLIC_API_URL,
  timeout: 45000,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json; charset=utf-8',
  },
  paramsSerializer: {
    indexes: null,
  },
});

let cachedSession: CachedSession | null = null;
const SESSION_CACHE_DURATION = 5 * 60 * 1000;

// Guests (no cookie session) are cached too, for a shorter window. Without
// this, every backend request from a signed-out visitor — most of our
// traffic — first ran `getServerSession`, a Server Action POST to the current
// page URL, so a video page paid one extra serial round trip per API call.
// 60s comfortably spans a page's settle burst (5-17s on phones in the
// CRE-1037 recordings) plus the first scroll pages. Every in-tab sign-in path
// drops it eagerly via `clearClientSessionCache`, so the TTL only bounds a
// sign-in that happened in ANOTHER tab — and that tab's UI stays signed-out
// until its own session query re-reads the cookie anyway.
const GUEST_SESSION_CACHE_DURATION = 60 * 1000;

let pendingSessionRefresh: Promise<AppSession | null> | null = null;

// In-flight cache-miss read. `getServerSession` is a Server Action round trip,
// and a cache miss usually coincides with a burst of queries (first paint, the
// 5-min lapse, the post-sign-in refetch storm) — without this, each one fires
// its own action and the App Router can end up running them back to back.
let pendingSessionRead: Promise<CachedSession> | null = null;

// Bumped every time the cache is dropped. A read that was already in flight
// resolves against the epoch it started in, so it can hand its value to the
// caller that asked for it without re-installing what we just invalidated.
let sessionEpoch = 0;

const invalidateCachedRead = (): void => {
  sessionEpoch += 1;
  cachedSession = null;
  pendingSessionRead = null;
};

/**
 * Forget the cached bearer (or the cached absence of one).
 *
 * MUST be called on every auth transition. `cachedSession` is a module
 * singleton in the browser bundle, and sign-in/sign-out are soft navigations
 * that never re-evaluate the module — so without this, the token minted for
 * the PREVIOUS user stays authoritative for up to `SESSION_CACHE_DURATION`
 * and gets attached to the next user's requests. It is not self-healing: the
 * backend has no logout/revocation, so the stale token answers 200 and the
 * 401 path below (the only other place the cache is dropped) never runs.
 *
 * Guests are cached too, so a sign-in that skips this would send the new
 * user's requests anonymously for up to `GUEST_SESSION_CACHE_DURATION`.
 *
 * Cheap by design — the next request re-reads the cookie.
 */
export const clearClientSessionCache = (): void => {
  invalidateCachedRead();
  pendingSessionRefresh = null;
};

const readSessionIntoCache = async (): Promise<CachedSession> => {
  const epoch = sessionEpoch;
  const session = await getServerSession();

  const now = Date.now();
  const sessionExpiresAt = session?.expiresAt || 0;
  const maxCacheSessionTime = now + SESSION_CACHE_DURATION;

  let cacheExpiresAt: number;
  if (!session?.accessToken) {
    // Signed out: there is no token lifetime to bound the entry by.
    cacheExpiresAt = now + GUEST_SESSION_CACHE_DURATION;
  } else {
    cacheExpiresAt =
      now < sessionExpiresAt && maxCacheSessionTime < sessionExpiresAt
        ? maxCacheSessionTime
        : sessionExpiresAt;
  }

  const read: CachedSession = {
    accessToken: session?.accessToken || null,
    refreshToken: session?.refreshToken || null,
    cacheExpiresAt,
  };

  // Cleared mid-flight (sign-out/sign-in landed while we were awaiting): this
  // value is already stale, so serve it to the awaiting request but leave the
  // cache empty for everyone after it.
  if (epoch === sessionEpoch) cachedSession = read;

  return read;
};

const getSessionWithCache = async (): Promise<CachedSession> => {
  const now = Date.now();

  // A token-less (guest) entry is a hit too — see GUEST_SESSION_CACHE_DURATION.
  if (cachedSession && now < cachedSession.cacheExpiresAt) {
    return cachedSession;
  }

  if (pendingSessionRead) return pendingSessionRead;

  const read: Promise<CachedSession> = readSessionIntoCache().finally(() => {
    // Retire only OUR read. A clear mid-flight may already have started a
    // newer one; nulling that would leave the next caller to open a third
    // read against the same cookie.
    if (pendingSessionRead === read) pendingSessionRead = null;
  });
  pendingSessionRead = read;

  return read;
};

const getFreshSession = async (): Promise<AppSession | null> => {
  if (pendingSessionRefresh) return pendingSessionRefresh;

  pendingSessionRefresh = (async () => {
    // Drops an in-flight plain read as well — the server just told us the
    // token it would install is no longer valid.
    invalidateCachedRead();
    try {
      // Force a refresh against the backend: a 401 means the token is no
      // longer valid server-side even if `expiresAt` says otherwise.
      return await getServerSession({ forceRefresh: true });
    } finally {
      pendingSessionRefresh = null;
    }
  })();

  return pendingSessionRefresh;
};

const clearExtensionSession = () => {
  window.postMessage(
    {
      origin: POST_MESSAGE_ORIGIN_TYPES.FROM_WEBAPP,
      to: POST_MESSAGE_DESTINATION_TYPES.TO_PUBLISHER,
      type: POST_MESSAGE_TYPES.USER_SESSION,
      message: null,
    },
    '*',
  );
};

axiosApiInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const session = await getSessionWithCache();

    if (session?.accessToken) {
      config.headers.Authorization = `Bearer ${session.accessToken}`;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

axiosApiInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;

    if (!originalRequest) {
      return Promise.reject(error);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (error.response?.status === 401 && !(originalRequest as any)._retry) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (originalRequest as any)._retry = true;

      const freshSession = await getFreshSession();

      if (freshSession?.accessToken && !freshSession.error) {
        originalRequest.headers.Authorization = `Bearer ${freshSession.accessToken}`;
        return axiosApiInstance(originalRequest);
      }

      clearExtensionSession();

      try {
        await signOutAction();
      } catch {
        // noop
      }

      // Hard redirect so the UI fully reflects the logged-out state — the
      // session cookie is gone but in-flight pages still hold authed data.
      if (typeof window !== 'undefined') {
        window.location.assign(HOME_ROUTE);
      }

      return Promise.reject(error);
    }

    return Promise.reject(error);
  },
);

const axiosClient = {
  get: <D, R>(
    url: string,
    config?: AxiosRequestConfig<D>,
  ): Promise<AxiosResponse<R | undefined, D>> =>
    axiosApiInstance.get(url, config),
  post: <D, R>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig<D>,
  ): Promise<AxiosResponse<R | undefined, D>> =>
    axiosApiInstance.post(url, data, config),
  patch: <D, R>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig<D>,
  ): Promise<AxiosResponse<R | undefined, D>> =>
    axiosApiInstance.patch(url, data, config),
  put: <D, R>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig<D>,
  ): Promise<AxiosResponse<R | undefined, D>> =>
    axiosApiInstance.put(url, data, config),
  delete: <D, R>(
    url: string,
    config?: AxiosRequestConfig<D>,
  ): Promise<AxiosResponse<R | undefined, D>> =>
    axiosApiInstance.delete(url, config),
};

export const axiosClientApiInstace = axiosApiInstance;

export default axiosClient;
